
import React, { useState, useEffect } from 'react';
import { UserProfile, TimelineEvent, DiaryEntry } from '../types';
import { Mic, MicOff, Play, Pause, Calendar, Award, Lightbulb, Heart, ArrowUp, Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { analyzeDiaryEntry, generatePeriodSummary } from '../services/geminiService';
import { TRANSLATIONS } from '../translations';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const TimelineView: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'timeline' | 'diary'>('timeline');
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognition, setRecognition] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  
  const t = TRANSLATIONS[user.language];

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const speechRecognition = new (window as any).webkitSpeechRecognition();
      speechRecognition.continuous = true;
      speechRecognition.lang = user.language === 'ckb' ? 'ckb-IQ' : 'fa-IR';
      speechRecognition.interimResults = true;
      speechRecognition.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        }
        if (finalTranscript) setTranscript(prev => prev + ' ' + finalTranscript);
      };
      setRecognition(speechRecognition);
    }
  }, [user.language]);

  const toggleRecording = () => {
    if (isRecording) { recognition?.stop(); setIsRecording(false); } 
    else { setTranscript(''); recognition?.start(); setIsRecording(true); }
  };

  const handleSaveDiary = async () => {
    if (!transcript.trim()) return;
    setIsAnalyzing(true);
    try {
        const analysis = await analyzeDiaryEntry(user, transcript);
        const newEntry: DiaryEntry = { id: Date.now().toString(), timestamp: Date.now(), transcript, category: analysis.category, keywords: analysis.keywords, aiInsight: analysis.insight, mood: analysis.mood };
        const newTimelineEvent: TimelineEvent = { id: `diary-${Date.now()}`, type: 'diary', title: `${t.diary_tab}: ${analysis.mood}`, description: transcript.substring(0, 50) + '...', timestamp: Date.now() };
        onUpdateUser({ ...user, diaryEntries: [newEntry, ...(user.diaryEntries || [])], timeline: [newTimelineEvent, ...(user.timeline || [])] });
        setTranscript('');
        setActiveTab('timeline');
    } catch (e) {
        console.error(e);
    } finally {
        setIsAnalyzing(false);
    }
  };

  const handleGenerateSummary = async () => {
      const summaryData = await generatePeriodSummary(user, 'هفتە', user.timeline || []);
      const summaryEvent: TimelineEvent = { id: `summary-${Date.now()}`, type: 'summary', title: 'کورتەی هەفتە', description: summaryData.stats, timestamp: Date.now(), metadata: summaryData };
      onUpdateUser({ ...user, timeline: [summaryEvent, ...(user.timeline || [])] });
  };

  const getEventIcon = (type: string) => {
      switch(type) {
          case 'achievement': return <Award className="w-5 h-5 text-amber-500" />;
          case 'insight': return <Lightbulb className="w-5 h-5 text-indigo-500" />;
          case 'memory': return <Heart className="w-5 h-5 text-pink-500" />;
          case 'diary': return <Mic className="w-5 h-5 text-blue-500" />;
          case 'summary': return <Sparkles className="w-5 h-5 text-purple-500" />;
          default: return <Calendar className="w-5 h-5 text-slate-500" />;
      }
  };

  const formatDate = (timestamp: number) => {
      return new Date(timestamp).toLocaleDateString(user.language === 'ckb' ? 'ckb-IR' : 'fa-IR', { month: 'long', day: 'numeric' });
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Calendar className="w-6 h-6 text-primary-500" />
             {t.life_path}
           </h2>
           <p className="text-slate-500 text-sm mt-1">{t.life_path_desc}</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button onClick={() => setActiveTab('timeline')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'timeline' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>{t.timeline_tab}</button>
            <button onClick={() => setActiveTab('diary')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'diary' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>{t.diary_tab}</button>
        </div>
      </div>

      {activeTab === 'diary' && (
          <div className="flex-1 flex flex-col bg-white rounded-3xl border border-slate-100 shadow-sm p-6 relative overflow-hidden">
               <div className="text-center mb-8 relative z-10">
                   <h3 className="text-xl font-bold text-slate-800 mb-2">{t.voice_diary}</h3>
                   <p className="text-slate-500">{t.diary_prompt}</p>
               </div>
               <div className="flex-1 flex flex-col items-center justify-center gap-8 relative z-10">
                   <button onClick={toggleRecording} className={`w-24 h-24 rounded-full flex items-center justify-center transition-all duration-500 shadow-2xl ${isRecording ? 'bg-red-500 scale-110 animate-pulse' : 'bg-primary-500 hover:scale-105'}`}>
                       {isRecording ? <MicOff className="w-10 h-10 text-white" /> : <Mic className="w-10 h-10 text-white" />}
                   </button>
                   <div className="w-full max-w-md bg-slate-50 rounded-2xl p-4 min-h-[150px] border border-slate-100 relative">
                       {transcript ? <p className="text-slate-700 leading-relaxed text-right">{transcript}</p> : <p className="text-slate-400 text-center mt-10">{isRecording ? t.listening : t.recording_prompt}</p>}
                       {!isRecording && <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} className="absolute inset-0 w-full h-full bg-transparent p-4 outline-none resize-none text-slate-700" />}
                   </div>
               </div>
               <div className="mt-auto pt-6 flex justify-center relative z-10">
                   <button onClick={handleSaveDiary} disabled={!transcript || isRecording || isAnalyzing} className="w-full max-w-md py-3 bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                       {isAnalyzing ? <Loader2 className="animate-spin" /> : <Sparkles className="w-4 h-4 text-yellow-400" />}
                       {isAnalyzing ? t.analyzing_smart : t.save_diary}
                   </button>
               </div>
          </div>
      )}

      {activeTab === 'timeline' && (
          <div className="flex-1 overflow-y-auto pr-2 relative">
               <div className="absolute top-0 bottom-0 right-6 w-0.5 bg-slate-200"></div>
               <div className="space-y-8 pb-8">
                   <div className="pl-4">
                       <button onClick={handleGenerateSummary} className="w-full py-3 border-2 border-dashed border-primary-200 rounded-xl text-primary-600 font-bold flex items-center justify-center gap-2">
                           <Sparkles className="w-5 h-5" /> {t.weekly_summary_btn}
                       </button>
                   </div>
                   {(!user.timeline || user.timeline.length === 0) && <div className="text-center py-10 text-slate-400"><Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>{t.no_events}</p></div>}
                   {user.timeline && user.timeline.map((event, index) => (
                       <div key={event.id} className="relative pr-14 animate-slide-in">
                           <div className="absolute right-3 top-3 w-6 h-6 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center z-10">{getEventIcon(event.type)}</div>
                           <div className={`p-5 rounded-2xl shadow-sm border border-slate-100 bg-white`}>
                               <div className="flex justify-between items-start mb-2"><h4 className="font-bold text-slate-800">{event.title}</h4><span className="text-xs text-slate-400">{formatDate(event.timestamp)}</span></div>
                               <p className="text-slate-600 text-sm line-clamp-2">{event.description}</p>
                           </div>
                       </div>
                   ))}
               </div>
          </div>
      )}
    </div>
  );
};

export default TimelineView;
